/**
 * Generic State Machine for Game Development
 *
 * Features:
 * - Type-safe states and transitions
 * - Enter/Exit/Update hooks per state
 * - Transition guards and conditions
 * - Event-driven architecture
 * - Hierarchical states (nested state machines)
 * - History tracking for debugging
 * - Optimized for 60fps (minimal allocations)
 */
export class StateMachine {
    id;
    states;
    currentStateId = null;
    context;
    onStateChange;
    // History for debugging (pre-allocated ring buffer)
    history;
    historyIndex = 0;
    historySize;
    constructor(config) {
        this.id = config.id;
        this.context = config.context;
        this.onStateChange = config.onStateChange;
        this.historySize = config.historySize ?? 50;
        // Pre-allocate history buffer
        this.history = new Array(this.historySize);
        for (let i = 0; i < this.historySize; i++) {
            this.history[i] = { from: null, to: '', timestamp: 0 };
        }
        // Build state map for O(1) lookup
        this.states = new Map();
        for (const state of config.states) {
            this.states.set(state.id, state);
        }
        // Validate initial state exists
        if (!this.states.has(config.initial)) {
            throw new Error(`Initial state "${config.initial}" not found in state machine "${this.id}"`);
        }
        // Enter initial state
        this.transitionTo(config.initial);
    }
    get current() {
        return this.currentStateId;
    }
    get currentState() {
        return this.currentStateId ? this.states.get(this.currentStateId) ?? null : null;
    }
    getContext() {
        return this.context;
    }
    setContext(partial) {
        Object.assign(this.context, partial);
    }
    /**
     * Check if machine is in a specific state
     */
    is(stateId) {
        return this.currentStateId === stateId;
    }
    /**
     * Check if transition to target state is allowed
     */
    can(targetStateId) {
        const currentState = this.currentState;
        if (!currentState?.transitions)
            return false;
        for (const transition of currentState.transitions) {
            if (transition.to === targetStateId) {
                if (!transition.guard || transition.guard(this.context)) {
                    return true;
                }
            }
        }
        return false;
    }
    /**
     * Attempt transition to target state.
     * Returns true if transition succeeded.
     *
     * `force` bypasses the allowed-transitions check — for void/reset transitions
     * (e.g. abandoning an in-flight round back to WAITING) that must succeed from
     * ANY state. onExit/onEnter still run; only the allow-list gate is skipped.
     */
    transitionTo(targetStateId, force = false) {
        const targetState = this.states.get(targetStateId);
        if (!targetState) {
            console.warn(`State "${targetStateId}" not found in machine "${this.id}"`);
            return false;
        }
        const currentState = this.currentState;
        const fromStateId = this.currentStateId;
        // Check transition is allowed (skip for initial state)
        if (currentState) {
            let transitionConfig = null;
            if (currentState.transitions) {
                for (const t of currentState.transitions) {
                    if (t.to === targetStateId) {
                        if (!t.guard || t.guard(this.context)) {
                            transitionConfig = t;
                            break;
                        }
                    }
                }
            }
            if (!transitionConfig && !force) {
                console.warn(`Transition from "${fromStateId}" to "${targetStateId}" not allowed in machine "${this.id}"`);
                return false;
            }
            // Exit current state
            currentState.onExit?.(this.context, targetStateId);
            // Run transition callback (none when forced past the allow-list)
            transitionConfig?.onTransition?.(this.context);
        }
        // Update current state
        this.currentStateId = targetStateId;
        // Record history (ring buffer, no allocation)
        const historyEntry = this.history[this.historyIndex];
        historyEntry.from = fromStateId;
        historyEntry.to = targetStateId;
        historyEntry.timestamp = performance.now();
        this.historyIndex = (this.historyIndex + 1) % this.historySize;
        // Notify listeners FIRST so they can update aux state (e.g. a
        // `previousState` field on the room) before onEnter fires. onEnter
        // handlers commonly broadcast the new state — if listeners ran
        // after, the broadcast would carry the stale previousState from
        // the prior transition, making per-shooter PLAYER_BETTING entries
        // (and similar same-state-named transitions) look like same-state
        // updates on the client.
        this.onStateChange?.(fromStateId, targetStateId, this.context);
        // Enter new state
        targetState.onEnter?.(this.context, fromStateId);
        return true;
    }
    /**
     * Force transition (bypasses guards) - use sparingly
     */
    forceTransitionTo(targetStateId) {
        const targetState = this.states.get(targetStateId);
        if (!targetState)
            return false;
        const fromStateId = this.currentStateId;
        const currentState = this.currentState;
        currentState?.onExit?.(this.context, targetStateId);
        this.currentStateId = targetStateId;
        // onStateChange BEFORE onEnter — see transitionTo for rationale.
        this.onStateChange?.(fromStateId, targetStateId, this.context);
        targetState.onEnter?.(this.context, fromStateId);
        return true;
    }
    /**
     * Update current state - call every frame
     * @param deltaTime Time since last frame in seconds
     */
    update(deltaTime) {
        this.currentState?.onUpdate?.(this.context, deltaTime);
    }
    /**
     * Get recent state history for debugging
     */
    getHistory() {
        const result = [];
        for (let i = 0; i < this.historySize; i++) {
            const entry = this.history[(this.historyIndex - 1 - i + this.historySize) % this.historySize];
            if (entry.timestamp > 0) {
                result.push({ ...entry });
            }
        }
        return result;
    }
    /**
     * Reset to initial state
     */
    reset(initialState) {
        const currentState = this.currentState;
        currentState?.onExit?.(this.context, initialState);
        this.currentStateId = null;
        this.transitionTo(initialState);
    }
}
//# sourceMappingURL=StateMachine.js.map